import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Tabs, Table, Tag, Space, Button, Input, Select,
    Typography, message, Alert, Tooltip, Empty, Descriptions, Progress, Popconfirm,
} from 'antd';
import {
    ReloadOutlined, PlusOutlined, EyeOutlined, WifiOutlined, DeleteOutlined,
    DatabaseOutlined, WalletOutlined, ShoppingOutlined, CopyOutlined,
} from '@ant-design/icons';
import CustomCard from '../../components/Card';
import TelnaApiService from '../../APIServices/TelnaApiService';
import OneApiService from '../../APIServices/OneApiService';
import CreateTelnaOrderModal from './CreateTelnaOrderModal';
import TelnaOrderDrawer from './TelnaOrderDrawer';
import {
    formatBytes, formatDate, formatDateShort, formatMoney, formatValidity, formatSeconds,
    ORDER_STATUS_COLOR, SIM_STATUS_COLOR, PROFILE_STATE_COLOR, PACKAGE_STATUS_COLOR,
} from './telnaHelpers';

const { Text, Title } = Typography;

/**
 * Telna eSIMs — the admin surface for the second eSIM provider.
 *
 * Telna sits alongside eSIM Access; neither page affects the other, and the
 * eSIM Access order flow under "Website Orders" is untouched.
 *
 * WHAT IS DIFFERENT HERE, and why the page is laid out this way
 *
 * Telna is a pre-loaded inventory, not a marketplace. We own a finite pool of
 * ICCIDs up front, and selling one means attaching a data package to it. So
 * unlike an eSIM Access page, the two numbers that matter most are STOCK and
 * WALLET, not a catalogue price — Telna's API publishes no price at all.
 *
 * "Sellable" is the tile to watch. It is not Telna's `pre-service` count: a SIM
 * we have already promised to a customer still reads `pre-service` upstream
 * until traffic flows, so the backend subtracts local reservations. Showing the
 * provider's raw number would advertise stock we cannot actually sell.
 */

const REFRESH_MS = 60000;

const TelnaEsims = () => {
    const [tab, setTab] = useState('overview');
    const [stats, setStats] = useState(null);
    const [status, setStatus] = useState(null);
    const [loadingStats, setLoadingStats] = useState(false);
    const [problem, setProblem] = useState(null);

    const [createOpen, setCreateOpen] = useState(false);
    const [drawer, setDrawer] = useState({ open: false, orderNumber: null });

    /* ------------------------------------------------------------ overview -- */

    const loadStats = useCallback(async (silent = false) => {
        if (!silent) setLoadingStats(true);
        try {
            const [s, st] = await Promise.all([
                TelnaApiService.getOrderStats(),
                TelnaApiService.getStatus().catch(() => null),
            ]);
            setStats(s);
            setStatus(st);
            setProblem(null);
        } catch (err) {
            setProblem({ message: 'Could not read Telna.', description: err.message });
        } finally {
            if (!silent) setLoadingStats(false);
        }
    }, []);

    useEffect(() => { loadStats(); }, [loadStats]);

    // Stock and wallet move without us doing anything (a customer installing an
    // eSIM flips it to in-service), so the tiles refresh themselves.
    useEffect(() => {
        const t = setInterval(() => loadStats(true), REFRESH_MS);
        return () => clearInterval(t);
    }, [loadStats]);

    /* -------------------------------------------------------------- orders -- */

    const [orders, setOrders] = useState([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [ordersPage, setOrdersPage] = useState({ current: 1, pageSize: 20, total: 0 });

    /**
     * THE REAL SALES, which are not `TelnaOrder` rows.
     *
     * `TelnaOrder` holds orders raised in this panel for manual fulfilment.
     * An app or website sale that Telna fulfils is a OneOrder with a
     * OneEsimLine, so the Orders tab was empty for every actual customer.
     * This lists the LINES, which is the question being asked of this page:
     * which ICCID, how much was sold, how much is left, was it issued.
     */
    const [sales, setSales] = useState([]);
    const [salesLoading, setSalesLoading] = useState(false);
    const [orderSearch, setOrderSearch] = useState('');
    const [orderStatus, setOrderStatus] = useState();

    // Destructured first so the memo depends on the two primitives it actually
    // uses. Depending on `ordersPage` itself would rebuild the params (and
    // refetch) every time `total` changes, which happens on every fetch.
    const { current: ordersCurrent, pageSize: ordersPageSize } = ordersPage;
    const orderParams = useMemo(() => {
        const p = { page: ordersCurrent, limit: ordersPageSize };
        if (orderSearch) p.search = orderSearch;
        if (orderStatus) p.status = orderStatus;
        return p;
    }, [ordersCurrent, ordersPageSize, orderSearch, orderStatus]);

    const loadSales = useCallback(async () => {
        setSalesLoading(true);
        try {
            setSales(await OneApiService.listEsimLines({ provider: 'telna', limit: 200 }));
        } catch (err) {
            message.error(err.message);
            setSales([]);
        } finally {
            setSalesLoading(false);
        }
    }, []);

    const loadOrders = useCallback(async () => {
        setOrdersLoading(true);
        try {
            const res = await TelnaApiService.getOrders(orderParams);
            setOrders(res.orders || []);
            setOrdersPage((p) => ({ ...p, total: res.pagination?.total || 0 }));
        } catch (err) {
            message.error(err.message);
            setOrders([]);
        } finally {
            setOrdersLoading(false);
        }
    }, [orderParams]);

    useEffect(() => {
        if (tab !== 'orders') return;
        loadOrders();
        loadSales();
    }, [tab, loadOrders, loadSales]);

    /** Stop the data at Telna, then refresh. */
    const stopSale = async (row) => {
        try {
            await OneApiService.stopEsimLine(row.id);
            message.success(`${row.iccid || row.orderNumber} stopped`);
            loadSales();
        } catch (err) {
            // A failed stop means the SIM may still be serving data, which is
            // the one failure on this page that costs money unnoticed.
            message.error(err.message, 8);
        }
    };

    /** Take it out of the customer's app. Does not stop the SIM. */
    const hideSale = async (row, unhide = false) => {
        try {
            await OneApiService.hideEsimLine(row.id, unhide);
            message.success(unhide ? 'Restored to the app' : 'Removed from the app');
            loadSales();
        } catch (err) {
            message.error(err.message);
        }
    };

    const salesColumns = [
        {
            title: 'Order',
            key: 'order',
            render: (_, r) => (
                <div>
                    <Text strong>{r.orderNumber}</Text>
                    <div><Text type="secondary" style={{ fontSize: 12 }}>{r.email || 'guest'}</Text></div>
                </div>
            ),
        },
        {
            title: 'eSIM',
            key: 'iccid',
            render: (_, r) => (r.iccid ? (
                <Space>
                    <Text code style={{ fontSize: 11 }}>{r.iccid}</Text>
                    <Button size="small" type="link" onClick={() => openSim(r.iccid)}>view</Button>
                </Space>
            ) : <Tag color="red">not issued</Tag>),
        },
        { title: 'Destination', dataIndex: 'countryName' },
        {
            title: 'Data',
            key: 'data',
            render: (_, r) => (
                <div style={{ minWidth: 150 }}>
                    <Progress
                        percent={r.totalMb ? Math.round((r.usedMb / r.totalMb) * 100) : 0}
                        size="small"
                        status={r.remainingMb <= 0 ? 'exception' : 'active'}
                    />
                    <Text style={{ fontSize: 12 }}>
                        {r.usedMb} MB used · <b>{r.remainingMb} MB left</b> of {r.totalMb} MB
                    </Text>
                </div>
            ),
        },
        {
            title: 'State',
            key: 'state',
            render: (_, r) => (
                <Space direction="vertical" size={2}>
                    <Tag color={SIM_STATUS_COLOR[r.status] || (r.status === 'active' ? 'green' : 'default')}>
                        {r.status}
                    </Tag>
                    {!r.issued && <Tag color="red">no activation code</Tag>}
                    {r.hidden && <Tag>hidden from app</Tag>}
                    {r.fundedUsd == null && r.issued && <Tag color="orange">never funded</Tag>}
                </Space>
            ),
        },
        {
            title: 'Checked',
            dataIndex: 'observedAt',
            render: (d) => <Text type="secondary" style={{ fontSize: 12 }}>{formatDate(d)}</Text>,
        },
        {
            title: '',
            key: 'actions',
            render: (_, r) => (
                <Space>
                    {r.status !== 'cancelled' && (
                        <Popconfirm
                            title="Stop this eSIM?"
                            description="Data stops immediately at Telna. The balance is left on the SIM, so this can be undone by re-provisioning."
                            okText="Stop it"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => stopSale(r)}
                        >
                            <Button size="small" danger>Stop</Button>
                        </Popconfirm>
                    )}
                    {r.hidden ? (
                        <Button size="small" onClick={() => hideSale(r, true)}>Restore</Button>
                    ) : (
                        <Popconfirm
                            title="Remove from the customer's app?"
                            description="The record is kept for invoice reconciliation. This does NOT stop the SIM — stop it first if it is live."
                            okText="Remove"
                            onConfirm={() => hideSale(r)}
                        >
                            <Button size="small">Remove</Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    /* ----------------------------------------------------------- catalogue -- */

    const [templates, setTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);

    const loadTemplates = useCallback(async () => {
        setTemplatesLoading(true);
        try {
            const res = await TelnaApiService.getPackageTemplates();
            setTemplates(res.templates || []);
        } catch (err) {
            message.error(err.message);
        } finally {
            setTemplatesLoading(false);
        }
    }, []);

    useEffect(() => { if (tab === 'catalogue') loadTemplates(); }, [tab, loadTemplates]);

    /* ----------------------------------------------------------- inventory -- */

    const [sims, setSims] = useState([]);
    const [simsLoading, setSimsLoading] = useState(false);
    const [simStatusFilter, setSimStatusFilter] = useState();
    const [simDetail, setSimDetail] = useState(null);

    const loadSims = useCallback(async () => {
        setSimsLoading(true);
        try {
            const res = await TelnaApiService.getSims(simStatusFilter ? { status: simStatusFilter } : {});
            setSims(res.sims || []);
        } catch (err) {
            message.error(err.message);
            setSims([]);
        } finally {
            setSimsLoading(false);
        }
    }, [simStatusFilter]);

    useEffect(() => { if (tab === 'inventory') loadSims(); }, [tab, loadSims]);

    const openSim = async (iccid) => {
        setSimDetail({ iccid, loading: true });
        try {
            const res = await TelnaApiService.getSim(iccid);
            setSimDetail({ iccid, loading: false, ...res });
        } catch (err) {
            message.error(err.message);
            setSimDetail(null);
        }
    };

    const copy = async (value, label) => {
        try { await navigator.clipboard.writeText(value); message.success(`${label} copied`); }
        catch { message.info(value); }
    };

    /* ------------------------------------------------------------- columns -- */

    const orderColumns = [
        {
            title: 'Order',
            dataIndex: 'order_number',
            render: (n) => <Text strong>{n}</Text>,
        },
        {
            title: 'Customer',
            key: 'customer',
            render: (_, r) => (
                <div>
                    <div>{r.customer_name || '—'}</div>
                    <Text type="secondary" style={{ fontSize: 12 }}>{r.customer_email}</Text>
                </div>
            ),
        },
        {
            title: 'eSIMs',
            key: 'items',
            width: 110,
            render: (_, r) => {
                const done = (r.items || []).filter((i) => i.status === 'completed').length;
                return <Text>{done} / {(r.items || []).length}</Text>;
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            width: 120,
            render: (s) => <Tag color={ORDER_STATUS_COLOR[s] || 'default'}>{s}</Tag>,
        },
        {
            title: 'Price',
            key: 'price',
            width: 110,
            render: (_, r) => formatMoney(r.price, r.currency),
        },
        { title: 'Source', dataIndex: 'source', width: 100 },
        {
            title: 'Email',
            dataIndex: 'email_sent',
            width: 90,
            render: (sent) => (sent ? <Tag color="green">sent</Tag> : <Tag>no</Tag>),
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            width: 160,
            render: (d) => formatDateShort(d),
        },
        {
            title: '',
            key: 'action',
            width: 60,
            render: (_, r) => (
                <Button type="text" icon={<EyeOutlined />} onClick={() => setDrawer({ open: true, orderNumber: r.order_number })} />
            ),
        },
    ];

    const templateColumns = [
        { title: 'Plan', dataIndex: 'name', render: (n) => <Text strong>{n}</Text> },
        { title: 'Data', dataIndex: 'dataAllowanceBytes', width: 110, render: (b) => formatBytes(b) },
        { title: 'Validity', key: 'validity', width: 110, render: (_, r) => formatValidity(r) },
        {
            title: 'Activation',
            dataIndex: 'activationType',
            width: 120,
            render: (t) => (
                <Tooltip title={t === 'AUTO'
                    ? 'Starts itself on first use — do not activate by hand or the validity clock starts early'
                    : 'Sits at NOT_ACTIVE until explicitly activated'}>
                    <Tag color={t === 'AUTO' ? 'green' : 'blue'}>{t || '—'}</Tag>
                </Tooltip>
            ),
        },
        {
            title: 'Activation window',
            dataIndex: 'activationWindowSeconds',
            width: 140,
            render: (s) => (
                <Tooltip title="How long after purchase the package may still be started. Past this it is dead and the money is spent.">
                    <span>{formatSeconds(s)}</span>
                </Tooltip>
            ),
        },
        { title: 'Countries', key: 'countries', width: 100, render: (_, r) => (r.supportedCountriesIso3?.length || 0) },
        { title: 'Status', dataIndex: 'status', width: 110, render: (s) => <Tag color={s === 'Active' ? 'green' : 'default'}>{s || '—'}</Tag> },
        // Deliberately no price column: Telna's API has none. See the modal.
    ];

    const simColumns = [
        {
            title: 'ICCID',
            dataIndex: 'iccid',
            render: (iccid) => (
                <Space size={4}>
                    <Text code style={{ fontSize: 12 }}>{iccid}</Text>
                    <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => copy(iccid, 'ICCID')} />
                </Space>
            ),
        },
        {
            title: 'Telna status',
            dataIndex: 'simStatus',
            width: 160,
            render: (s) => <Tag color={SIM_STATUS_COLOR[s] || 'default'}>{s}</Tag>,
        },
        {
            title: 'Sellable',
            dataIndex: 'available',
            width: 110,
            render: (a) => (a ? <Tag color="green">yes</Tag> : <Tag color="default">no</Tag>),
        },
        {
            title: 'Assigned to',
            key: 'assigned',
            render: (_, r) => (r.localRecord
                ? (
                    <div>
                        <div>{r.localRecord.customer_email || '—'}</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {r.localRecord.order_ref || '—'} · {r.localRecord.status}
                        </Text>
                    </div>
                )
                : <Text type="secondary">unassigned</Text>),
        },
        {
            title: '',
            key: 'action',
            width: 110,
            render: (_, r) => (
                <Space size={0}>
                    <Button type="text" icon={<EyeOutlined />} onClick={() => openSim(r.iccid)} />
                    {/*
                        REMOVING A SIM FROM OUR LEDGER, which is not the same
                        as cancelling it at Telna — there is no such call. A
                        SIM that was claimed and never delivered is released
                        and returns to circulation; one that WAS delivered has
                        an activation code out in the world that cannot be
                        revoked, and the server refuses unless forced. The
                        confirmation below says which case this is.
                    */}
                    <Popconfirm
                        title={r.localRecord ? 'Remove this eSIM?' : 'Nothing to remove'}
                        description={r.localRecord
                            ? (r.localRecord.status === 'reserved'
                                ? 'It was claimed but never delivered, so it returns to circulation.'
                                : 'It has been DELIVERED. Its activation code is out and cannot be revoked, and the data it was funded with is spent — this only deletes our record of who holds it.')
                            : 'This SIM is not assigned to anyone here.'}
                        okText={r.localRecord?.status === 'reserved' ? 'Release' : 'Remove anyway'}
                        okButtonProps={{ danger: r.localRecord?.status !== 'reserved' }}
                        disabled={!r.localRecord}
                        onConfirm={() => removeSim(r.iccid, r.localRecord?.status !== 'reserved')}
                    >
                        <Button
                            type="text"
                            danger
                            disabled={!r.localRecord}
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    /**
     * Take a SIM out of our ledger.
     *
     * `force` is passed explicitly rather than always: the server refuses a
     * delivered eSIM without it, and that refusal is the guard. Sending it
     * every time would turn a deliberate safety check into a formality.
     */
    const removeSim = useCallback(async (iccid, force) => {
        try {
            await TelnaApiService.removeEsim(iccid, force);
            message.success(force ? `Removed ${iccid}` : `Released ${iccid} back to stock`);
            loadSims();
            loadStats();
        } catch (err) {
            // The server's own words. It explains WHY a delivered eSIM is
            // refused, which a generic failure message would throw away.
            message.error(err?.response?.data?.message || err.message || 'Could not remove that eSIM');
        }
    }, [loadSims, loadStats]);

    /* ---------------------------------------------------------------- view -- */

    const provisioningEnabled = Boolean(stats?.provisioningEnabled);
    const available = stats?.stock?.available;

    const overview = (
        <>
            {problem && (
                <Alert type="error" showIcon style={{ marginBottom: 16 }} message={problem.message} description={problem.description} />
            )}

            {status && !status.reachable && (
                <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="The backend cannot reach Telna"
                    description={`Reason: ${status.reason}. Check TELNA_API_KEY on the server.`}
                />
            )}

            {stats && !provisioningEnabled && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Provisioning is switched off — no eSIM can be bought"
                    description="Orders can be created, listed and cancelled. Buying an eSIM draws down a prepaid wallet and this API has no refund call, so purchases stay blocked until TELNA_PROVISIONING_ENABLED=true is set on the server."
                />
            )}

            {/*
                THE FOUR STAT CARDS USED TO BE HERE.
                ---------------------------------------------------------
                They took a full row above the fold to say four numbers,
                three of which are repeated by the tables below — the stock
                count by Inventory, the order count by Orders, the issued
                count by the eSIM list. The fourth, the wallet, was the only
                one that could not be read anywhere else.

                They are now tags beside the Connection heading, where they
                sit next to the account they describe and cost no vertical
                space. The page opens on the tables, which is what the
                operator came for.
            */}

            {status && (
                <CustomCard
                    title={(
                        <Space wrap size={[8, 4]} style={{ width: '100%', justifyContent: 'space-between' }}>
                            <span>Connection</span>
                            <Space wrap size={[6, 4]}>
                                {/* The stock figure carries its own colour, because
                                    zero sellable SIMs is not a statistic — it is a
                                    shop that cannot fulfil the next order. */}
                                <Tooltip title="Pre-service SIMs minus the ones already promised to a customer. This is what can actually be sold right now.">
                                    <Tag
                                        icon={<WifiOutlined />}
                                        color={available === 0 ? 'red' : available <= 3 ? 'orange' : 'green'}
                                    >
                                        {available ?? '—'} sellable
                                        {stats?.stock ? ` · ${stats.stock.total} total` : ''}
                                    </Tag>
                                </Tooltip>
                                <Tag icon={<ShoppingOutlined />}>
                                    {stats?.orders?.total ?? '—'} orders
                                </Tag>
                                <Tooltip title="ICCIDs handed to a customer">
                                    <Tag icon={<DatabaseOutlined />}>
                                        {stats?.esimsIssued ?? '—'} issued
                                    </Tag>
                                </Tooltip>
                                {/* IN DOLLARS. Telna states this in cents, and the
                                    panel printed it raw — so a SIM holding 12.5c of
                                    data read "$12.5000" for a 100 MB sale. The
                                    backend converts it now; this only has to say
                                    which currency it is, which it never did. */}
                                <Tooltip title="Telna prepaid balance, per the contracted rate card">
                                    <Tag icon={<WalletOutlined />} color="blue">
                                        {stats?.wallet?.balance != null
                                            ? `$${Number(stats.wallet.balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                            : '—'}
                                    </Tag>
                                </Tooltip>
                            </Space>
                        </Space>
                    )}
                >
                    <Descriptions bordered size="small" column={2}>
                        <Descriptions.Item label="Reachable">
                            <Tag color={status.reachable ? 'green' : 'red'}>{status.reachable ? 'yes' : 'no'}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="API base">{status.base}</Descriptions.Item>
                        <Descriptions.Item label="Company">{status.companies?.[0]?.name || status.companyId || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Inventory">{status.inventoryId || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Group">{status.groupId || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Wallet">{status.walletId || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Provisioning">
                            <Tag color={provisioningEnabled ? 'green' : 'orange'}>
                                {provisioningEnabled ? 'enabled' : 'disabled'}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Key">
                            <Text code style={{ fontSize: 11 }}>{status.keyFingerprint || '—'}</Text>
                        </Descriptions.Item>
                    </Descriptions>
                </CustomCard>
            )}
        </>
    );

    return (
        <div style={{ padding: 20 }}>
            <Space style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap' }}>
                <div>
                    <Title level={3} style={{ margin: 0 }}>Telna eSIMs</Title>
                    <Text type="secondary">
                        Second eSIM provider. Pre-loaded inventory — an order attaches a data package to an ICCID we already own.
                    </Text>
                </div>
                <Space>
                    <Button icon={<ReloadOutlined />} loading={loadingStats} onClick={() => loadStats()}>Refresh</Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>New order</Button>
                </Space>
            </Space>

            <Tabs
                activeKey={tab}
                onChange={setTab}
                items={[
                    { key: 'overview', label: 'Overview', children: overview },
                    {
                        key: 'orders',
                        label: 'Orders',
                        children: (
                            <CustomCard>
                                <Space style={{ marginBottom: 12, flexWrap: 'wrap' }}>
                                    <Input.Search
                                        allowClear
                                        placeholder="Order number, email, name or ICCID"
                                        style={{ width: 320 }}
                                        onSearch={(v) => { setOrderSearch(v); setOrdersPage((p) => ({ ...p, current: 1 })); }}
                                    />
                                    <Select
                                        allowClear
                                        placeholder="Status"
                                        style={{ width: 160 }}
                                        value={orderStatus}
                                        onChange={(v) => { setOrderStatus(v); setOrdersPage((p) => ({ ...p, current: 1 })); }}
                                        options={['draft', 'provisioning', 'completed', 'partial', 'failed', 'cancelled'].map((v) => ({ value: v, label: v }))}
                                    />
                                    <Button icon={<ReloadOutlined />} onClick={loadOrders}>Reload</Button>
                                </Space>
                                {/* THE REAL SALES FIRST. `TelnaOrder` below holds
                                    only orders raised in this panel; a customer's
                                    order is a OneOrder with a OneEsimLine, which
                                    is what this table lists. */}
                                <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Title level={5} style={{ margin: 0 }}>Customer eSIMs</Title>
                                    <Button size="small" icon={<ReloadOutlined />} onClick={loadSales}>Reload</Button>
                                </div>
                                <Table
                                    rowKey="id"
                                    size="small"
                                    loading={salesLoading}
                                    columns={salesColumns}
                                    dataSource={sales}
                                    scroll={{ x: 1100 }}
                                    pagination={{ pageSize: 20, showSizeChanger: true }}
                                    locale={{
                                        emptyText: (
                                            <Empty description="No Telna eSIMs sold yet" />
                                        ),
                                    }}
                                />

                                <Title level={5} style={{ marginTop: 24 }}>Orders raised in this panel</Title>
                                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                                    Manual Telna orders. App and website sales are in the table above.
                                </Text>
                                <Table
                                    rowKey="_id"
                                    size="small"
                                    loading={ordersLoading}
                                    columns={orderColumns}
                                    dataSource={orders}
                                    scroll={{ x: 1000 }}
                                    locale={{ emptyText: <Empty description="No Telna orders yet" /> }}
                                    pagination={{
                                        current: ordersPage.current,
                                        pageSize: ordersPage.pageSize,
                                        total: ordersPage.total,
                                        showSizeChanger: true,
                                        onChange: (current, pageSize) => setOrdersPage((p) => ({ ...p, current, pageSize })),
                                    }}
                                />
                            </CustomCard>
                        ),
                    },
                    {
                        key: 'catalogue',
                        label: 'Catalogue',
                        children: (
                            <CustomCard
                                title="Package templates"
                                extra={<Button icon={<ReloadOutlined />} onClick={loadTemplates}>Reload</Button>}
                            >
                                <Alert
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: 12 }}
                                    message="These plans are published by Telna, not by us"
                                    description="A template carries an allowance and a country list but no price — what a package costs comes from the contracted rate card and shows up as wallet drawdown. Retail price is set per order."
                                />
                                <Table
                                    rowKey="id"
                                    size="small"
                                    loading={templatesLoading}
                                    columns={templateColumns}
                                    dataSource={templates}
                                    scroll={{ x: 900 }}
                                    pagination={false}
                                    locale={{ emptyText: <Empty description="Telna has published no templates on this inventory" /> }}
                                    expandable={{
                                        expandedRowRender: (r) => (
                                            <div>
                                                <Text strong>Coverage ({r.supportedCountriesIso3?.length || 0} countries, ISO-3):</Text>
                                                <div style={{ marginTop: 8, maxHeight: 140, overflowY: 'auto' }}>
                                                    {(r.supportedCountriesIso3 || []).map((c) => (
                                                        <Tag key={c} style={{ marginBottom: 4 }}>{c}</Tag>
                                                    ))}
                                                </div>
                                                {r.apn && <div style={{ marginTop: 8 }}><Text type="secondary">APN: {r.apn}</Text></div>}
                                                {r.notes && <div style={{ marginTop: 4 }}><Text type="secondary">{r.notes}</Text></div>}
                                            </div>
                                        ),
                                    }}
                                />
                            </CustomCard>
                        ),
                    },
                    {
                        key: 'inventory',
                        label: 'Inventory',
                        children: (
                            <CustomCard
                                title="SIM stock"
                                extra={(
                                    <Space>
                                        <Select
                                            allowClear
                                            placeholder="Telna status"
                                            style={{ width: 180 }}
                                            value={simStatusFilter}
                                            onChange={setSimStatusFilter}
                                            options={['pre-service', 'in-service', 'waiting-for-assignment', 'terminated']
                                                .map((v) => ({ value: v, label: v }))}
                                        />
                                        <Button icon={<ReloadOutlined />} onClick={loadSims}>Reload</Button>
                                    </Space>
                                )}
                            >
                                <Alert
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: 12 }}
                                    message="Inventory is finite and there is no reorder API"
                                    description="Telna loads eSIMs into this inventory up front. A SIM already promised to a customer still reads pre-service here, which is why 'Sellable' is the column to trust."
                                />
                                <Table
                                    rowKey="iccid"
                                    size="small"
                                    loading={simsLoading}
                                    columns={simColumns}
                                    dataSource={sims}
                                    scroll={{ x: 800 }}
                                    pagination={{ pageSize: 20, showSizeChanger: true }}
                                    locale={{ emptyText: <Empty description="No SIMs in this inventory" /> }}
                                />

                                {simDetail && (
                                    <div style={{ marginTop: 20 }}>
                                        <Title level={5}>eSIM {simDetail.iccid}</Title>
                                        {simDetail.loading ? <Text type="secondary">Loading…</Text> : (
                                            <>
                                                <Descriptions bordered size="small" column={2}>
                                                    <Descriptions.Item label="Profile state">
                                                        <Tag color={PROFILE_STATE_COLOR[simDetail.esim?.profileState] || 'default'}>
                                                            {simDetail.esim?.profileState || '—'}
                                                        </Tag>
                                                    </Descriptions.Item>
                                                    <Descriptions.Item label="SIM status">
                                                        <Tag color={SIM_STATUS_COLOR[simDetail.esim?.simStatus] || 'default'}>
                                                            {simDetail.esim?.simStatus || '—'}
                                                        </Tag>
                                                    </Descriptions.Item>
                                                    <Descriptions.Item label="SM-DP+">{simDetail.esim?.smdpAddress || '—'}</Descriptions.Item>
                                                    <Descriptions.Item label="EID">{simDetail.esim?.eid || 'not installed yet'}</Descriptions.Item>
                                                    <Descriptions.Item label="Activation code" span={2}>
                                                        <Space>
                                                            <Text code style={{ fontSize: 11, wordBreak: 'break-all' }}>
                                                                {simDetail.esim?.activationCode || '—'}
                                                            </Text>
                                                            {simDetail.esim?.activationCode && (
                                                                <Button size="small" icon={<CopyOutlined />}
                                                                    onClick={() => copy(simDetail.esim.activationCode, 'Activation string')} />
                                                            )}
                                                        </Space>
                                                    </Descriptions.Item>
                                                    {/* WHERE PER-MEGABYTE DATA ACTUALLY LIVES.
                                                        A YouGuide One sale tops up the SIM's own
                                                        wallet; it does not attach a package. Without
                                                        this row a SIM carrying a freshly bought
                                                        100 MB showed only a terminated test package
                                                        from months ago, and read as a failed sale. */}
                                                    <Descriptions.Item label="Data balance" span={2}>
                                                        {simDetail.esim?.walletMb != null ? (
                                                            <Space>
                                                                <Tag color={simDetail.esim.walletMb > 0 ? 'green' : 'default'}>
                                                                    {simDetail.esim.walletMb} MB
                                                                </Tag>
                                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                                    on the SIM wallet
                                                                    {simDetail.esim.walletMode ? ` · mode ${simDetail.esim.walletMode}` : ''}
                                                                </Text>
                                                            </Space>
                                                        ) : (
                                                            <Text type="secondary">
                                                                No SIM wallet — this SIM has never been funded by the megabyte.
                                                            </Text>
                                                        )}
                                                    </Descriptions.Item>
                                                    {/* WHERE PER-MEGABYTE DATA LIVES, and whether it
                                                        is capped. A sale funds the SIM's own wallet;
                                                        it attaches no package. And `wallet_mode`
                                                        other than SIM means this SIM draws from the
                                                        shared Billing Group — Telna's doc: it keeps
                                                        serving until the GROUP runs out, so it has no
                                                        limit of its own. */}
                                                    <Descriptions.Item label="SIM wallet" span={2}>
                                                        <Space wrap>
                                                            <Tag color={simDetail.esim?.walletBalance > 0 ? 'green' : 'default'}>
                                                                {simDetail.esim?.walletBalance != null
                                                                    ? `$${Number(simDetail.esim.walletBalance).toFixed(4)}`
                                                                    : 'no SIM wallet'}
                                                            </Tag>
                                                            {simDetail.esim?.walletMode === 'SIM' ? (
                                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                                    capped by its own balance
                                                                </Text>
                                                            ) : (
                                                                <Text type="danger" style={{ fontSize: 12 }}>
                                                                    mode {simDetail.esim?.walletMode || '—'} — NOT capped, draws from the shared group balance
                                                                </Text>
                                                            )}
                                                        </Space>
                                                    </Descriptions.Item>
                                                    <Descriptions.Item label="Open data sessions" span={2}>
                                                        {(simDetail.openDataSessions || []).length
                                                            ? `${simDetail.openDataSessions.length} live`
                                                            : 'none'}
                                                    </Descriptions.Item>
                                                </Descriptions>

                                                {(simDetail.esim?.packages || []).length > 0 && (
                                                    <>
                                                    {/* Named, because a terminated test package from
                                                        months ago sitting above a live wallet balance
                                                        is read as the state of the SIM. */}
                                                    <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
                                                        Packages ever attached to this SIM. Per-megabyte sales do not
                                                        appear here — they fund the wallet above.
                                                    </Text>
                                                    <Table
                                                        style={{ marginTop: 6 }}
                                                        rowKey="id"
                                                        size="small"
                                                        pagination={false}
                                                        dataSource={simDetail.esim.packages}
                                                        columns={[
                                                            { title: 'Package', dataIndex: 'templateName' },
                                                            { title: 'Status', dataIndex: 'status', width: 120, render: (s) => <Tag color={PACKAGE_STATUS_COLOR[s] || 'default'}>{s}</Tag> },
                                                            { title: 'Data left', dataIndex: 'dataRemainingBytes', width: 120, render: (b) => formatBytes(b) },
                                                            { title: 'Expires', dataIndex: 'expiresAt', width: 170, render: (d) => formatDate(d) },
                                                        ]}
                                                    />
                                                    </>
                                                )}
                                                <Button style={{ marginTop: 12 }} onClick={() => setSimDetail(null)}>Close</Button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </CustomCard>
                        ),
                    },
                ]}
            />

            <CreateTelnaOrderModal
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                provisioningEnabled={provisioningEnabled}
                availableStock={available}
                onCreated={() => { loadStats(); if (tab === 'orders') loadOrders(); else setTab('orders'); }}
            />

            <TelnaOrderDrawer
                open={drawer.open}
                orderNumber={drawer.orderNumber}
                provisioningEnabled={provisioningEnabled}
                onClose={() => setDrawer({ open: false, orderNumber: null })}
                onChanged={() => { loadOrders(); loadStats(true); }}
            />
        </div>
    );
};

export default TelnaEsims;
